import { DatabaseDateFormat } from '@Types';
import { TimestampConversionType } from '@Types/CreateNewSystemConnectionRequest';
import { DateTime as LuxonDateTime } from 'luxon';

type CreateRangeRequest = {
	databaseTimezone: string;
	offsetMs: number;
	intervalRange: number;
};

type TsConversionConfig = {
	dateTimeFormat: DatabaseDateFormat;
	timestampConversion: TimestampConversionType;
};

type MakeRequest = CreateRangeRequest & Omit<TsConversionConfig, 'date'>;

export class TimeRange {
	private constructor() {
		// ? noop
	}

	private static makeTimeRange(request: CreateRangeRequest) {
		const { intervalRange, databaseTimezone, offsetMs } = request;
		const rangeEnd = LuxonDateTime.local()
			.setZone(databaseTimezone)
			.minus({ milliseconds: offsetMs })
			.set({ second: 0, millisecond: 0 });
		const rangeStart = rangeEnd.minus({ milliseconds: intervalRange });
		return { end: rangeEnd, start: rangeStart };
	}

	private static convertTs(
		date: LuxonDateTime,
		request: TsConversionConfig,
	): string {
		const { timestampConversion, dateTimeFormat } = request;
		const tsStr = date.toFormat('yyyy-LL-dd HH:mm:ss');
		const option: string = timestampConversion || dateTimeFormat;
		switch (option) {
			case 'DD-MMM-YYYY': // ? ORACLE (format)
			case 'TIMESTAMP_prefix': // ? Oracle (mode)
				return `TIMESTAMP'${tsStr}'`;
			case 'TS_function': // ? ClearSCADA (mode)
				return `{TS '${tsStr}'}`;
			case 'YYYY-MM-DD': // ? ANSI (format)
			case 'IMPLICIT':
			default:
				return `'${tsStr}'`;
		}
	}

	public static make(request: MakeRequest) {
		const { intervalRange, databaseTimezone, offsetMs } = request;
		const range = TimeRange.makeTimeRange({
			intervalRange,
			databaseTimezone,
			offsetMs,
		});
		const end = TimeRange.convertTs(range.end, request);
		const start = TimeRange.convertTs(range.start, request);
		return { end, start };
	}

	public static isOlderThan = (baseUtcTs: number, minutes: number) => {
		const diff = Date.now() - baseUtcTs;
		const diffInMins = diff / 1000 / 60;
		return diffInMins > minutes;
	};

	public static minutesDifference = (tsMillis: number) => {
		return Math.round((parseInt(`${tsMillis}`) - Date.now()) / 1000 / 60);
	};
}
